import { useQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';
import { useClientId } from '../../_hooks/useClientId';
import { Persona } from '../BeFi20.meta';
import { SimilarityDTO } from './useOverviewQuery';

export const BEFI20_DISABLE_ERROR_REGEX = /is not enabled/i;

export interface ComparisonItemsDTO {
  startActing: string;
  startTalking: string;
  whatTheyNeedFromYou: string;
  whatYouNeedFromThem: string;
}

export interface ComparedScoreDTO {
  description: string;
  partnerScore: number;
  score: number;
  comparisonItems: ComparisonItemsDTO;
}

export interface CategoryResultDTO {
  description: string;
  score: number;
}

export interface IndividualResultsDTO {
  header: string;
  highlightedCategory: Persona;
  categories: Record<Persona, CategoryResultDTO>;
  initials: string;
}

interface RangeDTO {
  header: string;
  low: string;
  high: string;
}

export type CategoryInfoDTO = Record<Persona, RangeDTO>;

export interface ComparisonResultDTO {
  name: string;
  partnerName: string;
  overallCompatibility: number;
  compatibilityStatus: string;
  compatibilityDescription: string;
  comparedScores: Record<Persona, ComparedScoreDTO>;
  partnerInitials: string;
}

export interface ComparisonDTO {
  id: string;
  name: string;
  similarity: SimilarityDTO;
}

interface SurveyResultsDTO {
  individualResults: IndividualResultsDTO;
  surveyId: string;
  categoryInfo: CategoryInfoDTO;
  contactAdvisorText: string;
  comparisonResult: ComparisonResultDTO | null;
  comparisons: ComparisonDTO[];
}

interface FetcherParams {
  clientId: number;
  compareToId?: string;
}

const fetchSurveyResults = async ({
  clientId,
  compareToId,
}: FetcherParams): Promise<SurveyResultsDTO> => {
  const [data] = await Promise.all([
    authFetch(
      urlWithParams(
        `/BeFi20/Survey/Results`,
        {
          clientId,
          compare: compareToId || null,
        },
        { skipNulls: true },
      ),
      {
        headers: { 'Content-type': 'application/json' },
      },
    ),
    new Promise(r => setTimeout(r, 1000)),
  ]);

  return data;
};

interface QueryParams {
  compareToId?: string;
}

export const useSurveyResultsQuery = ({ compareToId }: QueryParams) => {
  const clientId = useClientId();

  return useQuery(
    queryKeys.results(clientId, compareToId),
    () => fetchSurveyResults({ clientId, compareToId }),
    {
      cacheTime: 0,

      retry(failureCount, error) {
        // Do not retry when BeFi20 is disabled
        return !(BEFI20_DISABLE_ERROR_REGEX.test(error.message) || failureCount > 2);
      },
    },
  );
};
